import { inject, Injectable, NgZone } from '@angular/core';

import { NotificationService } from '../components/shared/notification';
import { ApiErrorNotifierService } from './api-error-notifier.service';

/**
 * Widgets on a dashboard resolve their data independently and within a few
 * hundred milliseconds of each other, so empty results arrive as a burst.
 */
const BATCH_WINDOW_MS = 500;

/**
 * How far back a request failure suppresses the no-data toast. Widgets flip to
 * their empty state from error callbacks too, and "we couldn't reach the
 * server" is the more useful of the two messages.
 */
const FAILURE_SUPPRESSION_MS = 3000;

/** How many array rows a console preview shows before it truncates. */
const PREVIEW_ROWS = 3;

/**
 * How far back the log looks for the API calls behind an empty screen.
 *
 * Most widgets report from a chart component that was handed its rows through
 * an `@Input` and knows nothing about the request that filled them, so the
 * endpoint and payload cannot come from the report itself — they come from the
 * HTTP layer, and this window decides which recorded calls are close enough in
 * time to be the cause. Wide enough to cover a slow render after the response
 * lands, narrow enough that the previous filter selection has dropped off.
 */
const REQUEST_LOOKBACK_MS = 8000;

/** How many recent requests are kept for the log. */
const REQUEST_BUFFER_SIZE = 40;

/** Longest a payload may render inside a table cell before it truncates. */
const PAYLOAD_CELL_CHARS = 160;

/**
 * What a widget can say about *why* it had nothing to draw.
 *
 * Optional throughout: a widget that passes nothing still batches and toasts
 * exactly as before, it just logs less. Supplying `data` is what turns the
 * console line from "this widget was empty" into something diagnosable — most
 * usefully when the data was not actually empty and something downstream
 * discarded it.
 *
 * {@link endpoint} and {@link payload} are worth passing from a widget that
 * makes its own request, since it can name the call exactly. A chart that was
 * handed its rows through an `@Input` cannot, and does not have to: the log
 * falls back to the calls the HTTP layer recorded in the same window.
 */
export interface NoDataContext {
  /** Why the widget concluded it had nothing to draw. */
  reason?: string;
  /** What the widget actually received, previewed in the console. */
  data?: unknown;
  /** The API call behind the empty state, where the widget made it itself. */
  endpoint?: string;
  /** The query that call carried — filters, keys, date range. */
  payload?: unknown;
}

/** A short, non-recursive description of what a widget was handed. */
function describeShape(value: unknown): string {
  if (value === undefined) {
    return 'undefined';
  }
  if (value === null) {
    return 'null';
  }
  if (Array.isArray(value)) {
    return `Array(${value.length})`;
  }
  if (typeof value === 'object') {
    return `Object(${Object.keys(value as object).length} keys)`;
  }
  return `${typeof value}: ${String(value)}`;
}

/** Long arrays are cut down so one empty dashboard cannot flood the console. */
function preview(value: unknown): unknown {
  if (!Array.isArray(value) || value.length <= PREVIEW_ROWS) {
    return value;
  }
  return [...value.slice(0, PREVIEW_ROWS), `…and ${value.length - PREVIEW_ROWS} more`];
}

/**
 * A payload as one short line, for a table cell. The full object is logged
 * underneath, so this only has to be enough to recognise the call by.
 */
function compactJson(value: unknown, maxLength = PAYLOAD_CELL_CHARS): string {
  if (value === undefined || value === null) {
    return '(none)';
  }

  let text: string;
  if (typeof value === 'string') {
    text = value;
  } else if (typeof FormData !== 'undefined' && value instanceof FormData) {
    // A multipart upload: the field names are the useful part, and the file
    // contents are the last thing that belongs in a console line.
    //
    // Walked with `forEach` rather than `keys()`: this project's `lib` is
    // `es2020` + `dom` without `dom.iterable`, so the iterator methods are not
    // declared on `FormData` here. A repeated field is listed once.
    const fields: string[] = [];
    value.forEach((_entry, field) => {
      if (!fields.includes(field)) {
        fields.push(field);
      }
    });
    text = `FormData(${fields.join(', ')})`;
  } else {
    try {
      text = JSON.stringify(value) ?? String(value);
    } catch {
      // Circular, or a throwing `toJSON` — the shape still says something, and
      // nothing in a diagnostic is worth an exception.
      return describeShape(value);
    }
  }

  return text.length <= maxLength ? text : `${text.slice(0, maxLength - 1)}…`;
}

/** One completed data request, as the no-data log needs it. */
interface RecordedRequest {
  /** URL including query string, as the request was actually sent. */
  endpoint: string;
  /** The body it carried — where the filters and the api_key live. */
  payload: unknown;
  /** Rows the response carried, when its body was an array. */
  rows: number | null;
  /** What came back, for the bodies that are not arrays. */
  shape: string;
  at: number;
}

/** The last {@link REQUEST_BUFFER_SIZE} completed requests, oldest first. */
const recentRequests: RecordedRequest[] = [];

/**
 * Records a completed request so an empty screen can be traced back to the call
 * behind it. Called by the HTTP interceptor, the only place that sees both the
 * endpoint and the payload.
 *
 * Deliberately a module-level function rather than a method on the service:
 * this runs inside the request pipeline, and a diagnostic has no business
 * pulling the notifier — and the notification service behind it — into the
 * interceptor's injection graph. It also never throws, for the same reason. An
 * exception raised here would surface as a failed response for a request that
 * actually succeeded.
 */
export function recordDataRequest(endpoint: string, payload: unknown, body: unknown): void {
  try {
    recentRequests.push({
      endpoint,
      payload,
      rows: Array.isArray(body) ? body.length : null,
      shape: describeShape(body),
      at: Date.now(),
    });

    if (recentRequests.length > REQUEST_BUFFER_SIZE) {
      recentRequests.splice(0, recentRequests.length - REQUEST_BUFFER_SIZE);
    }
  } catch {
    // A lost log line is always cheaper than a broken request.
  }
}

/**
 * Raises a single "no data" notification on behalf of any number of widgets.
 *
 * A dashboard can have a dozen widgets come back empty from one over-narrow
 * filter. Toasting each of them would bury the screen, so reports are batched
 * inside {@link BATCH_WINDOW_MS} and collapsed into one toast carrying the
 * widget count. Anything arriving after the window reuses the same
 * notification key, so a late widget updates the toast in place rather than
 * stacking a second one.
 */
@Injectable({ providedIn: 'root' })
export class NoDataNotifierService {
  private readonly notify = inject(NotificationService);
  private readonly zone = inject(NgZone);
  private readonly apiErrors = inject(ApiErrorNotifierService);

  /**
   * Distinct widgets that reported empty in the current window, against
   * whatever each said about why. A repeat report replaces the earlier context,
   * so the most recent explanation is the one logged.
   */
  private readonly pending = new Map<string, NoDataContext | undefined>();
  private flushHandle: ReturnType<typeof setTimeout> | null = null;

  /**
   * Reports that `widget` rendered an empty state. Safe to call on every
   * re-render — repeats inside a window collapse to one toast.
   */
  report(widget: string, context?: NoDataContext): void {
    this.pending.set(widget, context);

    if (this.flushHandle !== null) {
      return;
    }

    // Charts report from inside their D3 render path, which frequently runs
    // outside Angular's zone; the notification service re-enters it for us.
    this.zone.runOutsideAngular(() => {
      this.flushHandle = setTimeout(() => {
        this.flushHandle = null;
        this.flush();
      }, BATCH_WINDOW_MS);
    });
  }

  /** Drops anything batched but not yet shown, e.g. when a filter is reset. */
  cancelPending(): void {
    this.pending.clear();

    if (this.flushHandle !== null) {
      clearTimeout(this.flushHandle);
      this.flushHandle = null;
    }
  }

  /**
   * Explains one batch in the console: which widgets came back empty, what each
   * was handed, and whether a toast followed.
   */
  private logReports(
    reports: Array<[string, NoDataContext | undefined]>,
    suppressed: boolean
  ): void {
    const label =
      `[no-data] ${reports.length} widget${reports.length === 1 ? '' : 's'} reported no data` +
      (suppressed
        ? ` — no toast: a request failed in the last ${FAILURE_SUPPRESSION_MS}ms, and that message is the more useful one`
        : '');

    console.groupCollapsed(label);

    console.table(
      reports.map(([widget, context]) => ({
        widget,
        reason: context?.reason ?? '(not reported)',
        endpoint: context?.endpoint ?? '(not reported)',
        payload:
          context && 'payload' in context ? compactJson(context.payload) : '(not reported)',
        received: context && 'data' in context ? describeShape(context.data) : '(not reported)',
      }))
    );

    this.logRecentRequests();

    for (const [widget, context] of reports) {
      if (context && 'payload' in context) {
        // The cell above is for scanning; this is the object you copy back into
        // a request to reproduce the empty result.
        console.log(
          `[no-data] ${widget} queried ${context.endpoint ?? '(endpoint not reported)'} with:`,
          context.payload
        );
      }

      if (!context || !('data' in context)) {
        continue;
      }

      // A widget that was handed rows and still drew nothing is the interesting
      // case: the data arrived and something downstream — a filter, a mapping, a
      // key mismatch — dropped it. Worth separating from a genuinely empty set.
      if (Array.isArray(context.data) && context.data.length > 0) {
        console.warn(
          `[no-data] ${widget} received ${context.data.length} row(s) but rendered empty — ` +
            'the data arrived, so look downstream of the request.',
          preview(context.data)
        );
      } else {
        console.log(`[no-data] ${widget} received:`, preview(context.data));
      }
    }

    console.groupEnd();
  }

  /**
   * The API calls that completed just before this batch — the endpoint each one
   * hit, the query it carried, and how much it came back with.
   *
   * This is what makes an empty dashboard diagnosable when the reports
   * themselves cannot name a call: a payload with a date range nothing falls
   * into, or a filter combination with no rows behind it, is visible here at
   * once. It is also how "the request came back empty" gets told apart from
   * "the request came back full and the widget still drew nothing".
   */
  private logRecentRequests(): void {
    const now = Date.now();
    const requests = recentRequests.filter((request) => now - request.at <= REQUEST_LOOKBACK_MS);

    if (requests.length === 0) {
      console.log(
        `[no-data] no API call completed in the last ${REQUEST_LOOKBACK_MS}ms — ` +
          'the widgets were empty before any response landed.'
      );
      return;
    }

    console.log(`[no-data] API calls completed in the last ${REQUEST_LOOKBACK_MS}ms:`);
    console.table(
      requests.map((request) => ({
        endpoint: request.endpoint,
        payload: compactJson(request.payload),
        returned: request.rows === null ? request.shape : `${request.rows} row(s)`,
        msAgo: now - request.at,
      }))
    );

    // Full payloads only for the calls that came back with nothing. Those are
    // the ones worth copying into a client to reproduce; printing all forty
    // would bury them.
    for (const request of requests) {
      if (request.rows === 0 || request.shape === 'null' || request.shape === 'undefined') {
        console.log(`[no-data] ${request.endpoint} returned nothing for:`, request.payload);
      }
    }
  }

  private flush(): void {
    const reports = [...this.pending.entries()];
    const count = reports.length;
    this.pending.clear();

    if (count === 0) {
      return;
    }

    // The widgets are empty because the request failed, and the failure has
    // already been reported with a far more actionable message.
    const suppressed = this.apiErrors.hasRecentFailure(FAILURE_SUPPRESSION_MS);

    // Logged either way. A suppressed toast is the case most likely to be
    // mistaken for a bug — the screen goes empty and says nothing — so the
    // console is where that decision is explained.
    this.logReports(reports, suppressed);

    if (suppressed) {
      return;
    }

    this.notify.warning(
      count === 1
        ? 'No data available for the selected filters.'
        : `No data available for the selected filters in ${count} widgets.`,
      'No data found.',
      {
        // One key for every widget: a late report replaces this toast instead
        // of stacking alongside it.
        key: 'no-data',
        duration: 7000,
      }
    );
  }

}
